// The filing numbers are computed on the server from the samples and prices already
// on screen. This module still chooses which samples and which state market to send.
import type { SampleEvent, SoilSamplePoint } from "../../types/contract";
import { STATE_PRICES } from "../../lib/statePrices";
import { LIME_KEY } from "./section180Nutrients";
import { postJson, postJsonForFile } from "../../api/httpGateway";
import type { Branding } from "../../lib/branding";

export interface Section180FieldInput {
  /* * `state`: two-letter US state of the field (centroid, from the stored centroid), 'NA' outside the 50 */
  field: {
    field_id: string;
    field_name: string;
    acres: number;
    state?: string;
    farm_name?: string;
  };
  /* * the CURRENT event's samples (caller filters, same rule as the sample layers) */
  samples: SoilSamplePoint[];
  eventKind: SampleEvent["kind"] | null;
  zonesFc?: GeoJSON.FeatureCollection | null;
  /* * field boundary geometry (FC/Feature/geometry) — sent with the filing, not drawn here */
  boundary?: unknown;
}

export const SECTION180_NUTRIENTS = [
  { key: "ph", label: "pH" },
  { key: "buffer_ph", label: "Buffer pH" },
  { key: "p", label: "P (M3, 8in ppm)" },
  { key: "k", label: "K (M3, 8in ppm)" },
  { key: "cec", label: "CEC" },
  { key: "om", label: "OM (%)" },
] as const;

/*
 * The samples the CURRENT maps are built from — EXACTLY `soilLayers`' current/old split rule:
 * OLD = attributed to a DIFFERENT results-bearing, non-current event; everything else (incl.
 * unattributed) = current. Returns the current event too, for its `kind`.
 */
export function currentSamples(
  samples: SoilSamplePoint[],
  events: SampleEvent[],
): { samples: SoilSamplePoint[]; event: SampleEvent | null } {
  const current = events.find((e) => e.is_current) ?? null;
  const currentId = current?.event_id;
  if (!currentId) return { samples, event: null };
  const byId = new Map(events.map((e) => [e.event_id, e]));
  const kept = samples.filter((s) => {
    const eid = s.sample_event_id ?? null;
    const ev = eid != null ? byId.get(eid) : undefined;
    return !(
      eid != null &&
      eid !== currentId &&
      ev?.has_results &&
      !ev.is_current
    );
  });
  return { samples: kept, event: current };
}

/* * "has maps" gate: at least one sample carries at least one of the claim nutrients */
export function fieldHasMaps(samples: SoilSamplePoint[]): boolean {
  return samples.some((s) =>
    SECTION180_NUTRIENTS.some(({ key }) => {
      const v =
        (s.properties_8in as Record<string, unknown> | null | undefined)?.[
          key
        ] ??
        (s.properties as Record<string, unknown> | null | undefined)?.[key];
      return typeof v === "number" && Number.isFinite(v);
    }),
  );
}

// BUILD-UP thresholds — the ppm level above which a soil test counts as excess.
// K is CEC-tiered: below 5 takes 100, 5 and above takes 120. Unknown CEC takes
// the high tier, which claims less excess.
export const SECTION180_P_THRESHOLD = 20;
export const SECTION180_K_THRESHOLDS = {
  lowCec: 100,
  highCec: 120,
  cecBreak: 5,
} as const;
export const SECTION180_P_FACTORS = {
  lbPerPpm: 2.6,
  oxideFromElemental: 2.29,
} as const;
export const SECTION180_K_FACTORS = {
  lbPerPpm: 2.67,
  oxideFromElemental: 1.205,
} as const;
export const SECTION180_PRODUCTS = {
  potash: { label: "Potash", oxideLbsPerTon: 1200, pricePerTon: 460 },
  phosphate: { label: "MAP", oxideLbsPerTon: 1040, pricePerTon: 845 },
} as const;

export interface Section180Prices {
  map: number;
  potash: number;
  phosphateProduct?: "MAP" | "DAP";
  phosphateLbsPerTon?: number;
}
// Every extra factor is user-supplied. A default would put an unowned number on a filing.
export interface Section180ExtraNutrient {
  key: string;
  label: string;
  product: string;
  threshold: number;
  lbsElementalPerPpm?: number;
  lbsNutrientPerLbElemental?: number;
  lbsNutrientPerTonProduct?: number;
  tonsPerTenthPh?: number;
  prices: Record<string, number>;
}

/* * Lime is quoted in tons per 0.1 pH of build, not through the elemental chain. */
export const isLimeNutrient = (x: { key: string }): boolean =>
  x.key === LIME_KEY;

export interface Section180Options {
  pThreshold: number;
  kThresholdLowCec: number;
  kThresholdHighCec: number;
  prices: Record<string, Section180Prices>;
  extras?: Section180ExtraNutrient[];
  /* * the market-value date the letter quotes — a claim can be filed for a past purchase */
  valueAsOf?: string;
}

export interface Section180Figure {
  field_id: string;
  excess: { total_value: number };
}

/* * The distinct states across a selection, in first-seen order ('NA' included — it is a real bucket). */
export function statesOf(inputs: { field: { state?: string } }[]): string[] {
  const out: string[] = [];
  for (const i of inputs) {
    const st = i.field.state ?? "NA";
    if (!out.includes(st)) out.push(st);
  }
  return out;
}

/*
 * The published market for a state, or the platform's standing figures when none exists. The
 * fallback only ever reaches a branded report — the ROGO Default refuses states the table
 * cannot price rather than borrowing another state's market.
 */
export function defaultPricesFor(state: string): Section180Prices {
  const row = STATE_PRICES[state];
  return row
    ? {
        map: row.phosphatePricePerTon,
        potash: row.potashPricePerTon,
        phosphateProduct: row.phosphateProduct,
        phosphateLbsPerTon: row.phosphateLbsPerTon,
      }
    : {
        map: SECTION180_PRODUCTS.phosphate.pricePerTon,
        potash: SECTION180_PRODUCTS.potash.pricePerTon,
        phosphateProduct: "MAP",
        phosphateLbsPerTon: SECTION180_PRODUCTS.phosphate.oxideLbsPerTon,
      };
}

export function defaultSection180Options(states: string[]): Section180Options {
  const prices: Record<string, Section180Prices> = {};
  for (const st of states.length ? states : ["NA"])
    prices[st] = defaultPricesFor(st);
  return {
    pThreshold: SECTION180_P_THRESHOLD,
    kThresholdLowCec: SECTION180_K_THRESHOLDS.lowCec,
    kThresholdHighCec: SECTION180_K_THRESHOLDS.highCec,
    prices,
  };
}

/* * A state's prices, falling back to that state's published market for one the options never named. */
export function priceFor(
  options: Section180Options,
  state: string | undefined,
): Section180Prices {
  return options.prices[state ?? "NA"] ?? defaultPricesFor(state ?? "NA");
}

/* * The phosphate product a row is priced in, and how much P2O5 a ton of it carries. */
export const phosphateOf = (
  price: Section180Prices,
): { product: "MAP" | "DAP"; lbsPerTon: number } => ({
  product: price.phosphateProduct ?? "MAP",
  lbsPerTon:
    price.phosphateLbsPerTon ?? SECTION180_PRODUCTS.phosphate.oxideLbsPerTon,
});

function localDate(now = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function filenameOf(header: string | null, fallback: string): string {
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header ?? "");
  if (encoded?.[1]) return decodeURIComponent(encoded[1]);
  const plain = /filename="([^"]+)"/i.exec(header ?? "");
  return plain?.[1] || fallback;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/* * The panel's per-field total is the figure the letter will print. */
export async function section180Figures(
  inputs: Section180FieldInput[],
  options: Section180Options,
): Promise<Section180Figure[]> {
  const data = await postJson<{ figures: Section180Figure[] }>(
    "/section-180/figures",
    { inputs, options },
  );
  return data.figures;
}

/*
 * The letter and the workbook are one zip. Prices travel with the request so the
 * server does not look up a state market of its own.
 */
export async function downloadSection180(
  inputs: Section180FieldInput[],
  fieldName: string | null,
  branding?: Branding | null,
  options?: Section180Options,
): Promise<void> {
  const chosen = options ?? defaultSection180Options(statesOf(inputs));
  const { blob, disposition } = await postJsonForFile("/section-180", {
    inputs,
    options: chosen,
    field_name: fieldName,
    local_date: localDate(),
    branding_name: branding?.name ?? null,
  });
  triggerDownload(blob, filenameOf(disposition, "Section-180.zip"));
}
